import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "../i18n";

import DataTableLoadingRow from "../Components/DataTableLoadingRow";
import { useNavigate } from "react-router-dom";
import { RoutesPaths, backendUrl } from "../Router/constant";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteVendorProductAction,
  getVendorProductsAction,
} from "../actions/vendorProduct";

function ProductTableDropdown({ label, value, options, onChange, disabled = false }) {
  const { text } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setIsOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div
      className={"inquiries-status-filter" + (isOpen ? " is-open" : "")}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="inquiries-status-trigger"
        aria-label={text(label)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{text(selectedOption?.label || label)}</span>
        <svg viewBox="0 0 14 14" aria-hidden="true">
          <path d="m3.5 5.25 3.5 3.5 3.5-3.5" />
        </svg>
      </button>
      {isOpen && !disabled && (
        <div className="inquiries-status-menu" role="listbox" aria-label={text(label)}>
          {options.map((option) => (
            <button
              type="button"
              key={option.value || "all"}
              className={"inquiries-status-option" + (value === option.value ? " is-selected" : "")}
              role="option"
              aria-selected={value === option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {text(option.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const stats = [
  {
    title: "Total",
    value: "24",
    change: "+3 created this month",
    icon: "/assets/product-icon.svg",
    color: "gold",
  },
  {
    title: "Active",
    value: "18",
    change: "75% currently active",
    icon: "/assets/product-icon.svg",
    color: "green",
  },
  {
    title: "Inactive",
    value: "4",
    change: "2 awaiting publication",
    icon: "/assets/product-icon.svg",
    color: "blue",
  },
  {
    title: "Alert",
    value: "2",
    change: "Retained for historical use",
    icon: "/assets/alert-icon.svg",
    color: "red",
  },
];

const toProductText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      return value.map((entry) => toProductText(entry)).filter(Boolean).join(", ");
    }
    if (typeof value === "object") {
      const nestedText = toProductText(
        value.name,
        value.title,
        value.productName,
        value.sectorName,
        value.label,
        value.value,
        value.code,
        value.symbol,
        value.description,
        value.text,
        value._id,
        value.id,
      );
      if (nestedText) return nestedText;
      continue;
    }
    return String(value);
  }
  return "";
};

const mapVendorProduct = (item) => {
  const product =
    (item?.product && typeof item.product === "object" ? item.product : null) ||
    (item?.productId && typeof item.productId === "object" ? item.productId : null) ||
    {};
  const sector =
    (item?.sector && typeof item.sector === "object" ? item.sector : null) ||
    (item?.sectorId && typeof item.sectorId === "object" ? item.sectorId : null) ||
    (product.sector && typeof product.sector === "object" ? product.sector : null) ||
    {};
  const productId =
    (typeof item?.productId === "string" || typeof item?.productId === "number"
      ? item.productId
      : "") ||
    product._id || product.id || item?.product?._id || "";
  const sectorId =
    (typeof item?.sectorId === "string" || typeof item?.sectorId === "number"
      ? item.sectorId
      : "") ||
    sector._id || sector.id || "";
  const apiId = String(item?.apiId || item?.vendorProductId || item?._id || item?.id || productId || "");
  const imageCandidates = [
    product.mainImage,
    item?.mainImage,
    product.mainImageId,
    item?.mainImageId,
    item?.images?.[0],
    product.imageId,
    item?.imageId,
    product.image,
    item?.image,
  ];
  const imageValue = imageCandidates
    .map((image) => {
      if (typeof image === "string") {
        const value = image.trim();
        return /^[a-z]:[\\/]/i.test(value) || /^\\\\/.test(value) ? "" : value;
      }
      if (!image || typeof image !== "object") return "";

      const uploadId =
        image.uploadId || image.fileId || image.imageId || image._id || image.id;
      if (uploadId) return uploadId;

      const imageUrl = image.url || image.path || "";
      return /^(https?:|data:|blob:|\/)/i.test(imageUrl) ? imageUrl : "";
    })
    .find(Boolean) || "";
  const imageUrl = imageValue
    ? /^(https?:|data:|blob:|\/)/i.test(imageValue)
      ? imageValue
      : backendUrl + "/uploads/" + encodeURIComponent(imageValue)
    : "";
  return {
    ...item,
    id: apiId,
    apiId,
    productId: String(productId),
    sectorId: String(sectorId),
    productName:
      toProductText(
        product.name,
        product.title,
        product.productName,
        item?.productName,
        item?.productsSupplied,
        item?.customProductName,
        item?.name,
        item?.templateName,
      ) || "\u2014",
    productCode: toProductText(
      item?.productCode,
      item?.templateId,
      product.productCode,
      product.code,
    ),
    category:
      toProductText(
        sector.name,
        sector.title,
        item?.sectorName,
        item?.category,
        item?.commodity,
      ) || "\u2014",
    price: item?.price ?? product.price ?? "",
    quantity: item?.quantity ?? product.quantity ?? 0,
    quantityUnit: toProductText(
      item?.quantityUnit,
      item?.unit,
      product.quantityUnit,
      product.unit,
    ),
    currency: toProductText(item?.currency, product.currency) || "USD",
    currencySymbol:
      toProductText(item?.currencySymbol, product.currencySymbol) ||
      getCurrencySymbol(
        toProductText(item?.currency, product.currency) || "USD",
      ),
    description: toProductText(item?.productDescriptions, item?.description, product.description),
    status:
      toProductText(item?.status) ||
      (item?.isActive === false ? "Inactive" : "Active"),
    mainImage: imageUrl,
    galleryImages: item?.galleryImages || [],
  };
};

const currencySymbols = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  INR: "₹",
  AED: "د.إ",
  SAR: "﷼",
  ZAR: "R",
};

function getCurrencySymbol(currency) {
  const value = String(currency || "");
  if (["$", "€", "£", "₹", "R"].includes(value)) return value;
  if (currencySymbols[value]) return currencySymbols[value];

  if (/^[A-Z]{3}$/.test(value)) {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: value,
      }).formatToParts(0).find((part) => part.type === "currency")?.value || value;
    } catch {
      return value;
    }
  }

  return value || "$";
}

export default function Product() {
  const { t, text, status: translateStatus } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    items: vendorProductItems = [],
    pagination = {},
    loading = false,
    error = "",
    mutationLoading = false,
  } = useSelector((state) => state.vendorProduct || {});
  const products = useMemo(
    () => vendorProductItems.map(mapVendorProduct),
    [vendorProductItems],
  );
  const [page, setPage] = useState(1);
  const limit = 10;

  useEffect(() => {
    dispatch(getVendorProductsAction({ page, limit }));
  }, [dispatch, page, limit]);

  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!statusDropdownRef.current?.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // NEW FILTERS
  const [sectorFilter, setSectorFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");

  const [deletePopup, setDeletePopup] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  /*
   * ==============================
   * UNIQUE SECTOR LIST
   * ==============================
   */
  const sectorOptions = useMemo(() => {
    const productList = Array.isArray(products) ? products : [];

    const sectors = productList
      .map((item) => item?.category || item?.commodity || "")
      .filter(Boolean);

    return [...new Set(sectors)].sort();
  }, [products]);
  const productOptions = useMemo(() => {
    const productList = Array.isArray(products) ? products : [];

    if (!sectorFilter) {
      return [];
    }

    return productList.filter((item) => {
      const sector = item?.category || item?.commodity || "";

      return sector === sectorFilter;
    });
  }, [products, sectorFilter]);
  const handleSectorChange = (value) => {
    setSectorFilter(value);
    setProductFilter("");
    setPage(1);
  };

  const currentPage = Number(pagination.page) || page;
  const total = Number(pagination.total) || 0;
  const totalPages = Math.max(Number(pagination.totalPages) || 1, 1);
  const pageWindowStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pageNumbers = Array.from(
    { length: Math.min(totalPages, 5) },
    (_, index) => pageWindowStart + index,
  );
  const firstItem = total ? (currentPage - 1) * limit + 1 : 0;
  const lastItem = total ? Math.min((currentPage - 1) * limit + products.length, total) : 0;

  const filteredProducts = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    const productList = Array.isArray(products) ? products : [];

    return productList.filter((item) => {
      const productName = toProductText(
        item?.productName,
        item?.templateName,
      ).toLowerCase();

      const productCode = toProductText(
        item?.productCode,
        item?.templateId,
      ).toLowerCase();

      const sector = toProductText(item?.category, item?.commodity);

      const description = toProductText(item?.description).toLowerCase();

      /*
       * SEARCH
       */
      const matchesSearch =
        !searchValue ||
        productName.includes(searchValue) ||
        productCode.includes(searchValue) ||
        sector.toLowerCase().includes(searchValue) ||
        description.includes(searchValue);

      /*
       * STATUS
       */
      const matchesStatus =
        !statusFilter ||
        String(item?.status || "").toLowerCase() === statusFilter.toLowerCase();

      /*
       * SECTOR
       */
      const matchesSector = !sectorFilter || sector === sectorFilter;

      /*
       * PRODUCT
       */
      const itemId = String(item?.id || item?._id || "");

      const matchesProduct = !productFilter || itemId === productFilter;

      return matchesSearch && matchesStatus && matchesSector && matchesProduct;
    });
  }, [products, search, statusFilter, sectorFilter, productFilter]);

  const activeCount = products.filter(
    (item) => String(item.status).toLowerCase() === "active",
  ).length;
  const productStats = stats.map((stat, index) => ({
    ...stat,
    value: String(
      index === 0
        ? total
        : index === 1
          ? activeCount
          : index === 2
            ? Math.max(total - activeCount, 0)
            : 0,
    ),
  }));

  /*
   * ==============================
   * ADD / VIEW / EDIT
   * ==============================
   */
  const openProductPage = (mode, item = null) => {
    if (mode === "add") {
      navigate(RoutesPaths.ADDPRODUCT);

      return;
    }

    if (!item) {
      return;
    }

    const product = {
      ...item,

      productName: item.productName || item.templateName || "",

      productCode: item.productCode || item.templateId || "",

      category: item.category || item.commodity || "",

      price: item.price ?? "",

      currency: item.currency || "USD",

      description: item.description || "",

      status: item.status?.toLowerCase() === "active" ? "active" : "draft",

      mainImage: item.mainImage || "",

      galleryImages: item.galleryImages || [],
    };

    window.sessionStorage.setItem("selectedProduct", JSON.stringify(product));

    if (mode === "view") {
      navigate(`/product/view/${item.id}`, {
        state: {
          product,
        },
      });

      return;
    }

    if (mode === "edit") {
      navigate(`/product/edit/${item.id}`, {
        state: {
          product,
        },
      });
    }
  };

  /*
   * ==============================
   * DELETE
   * ==============================
   */
  const openDeletePopup = (item) => {
    setSelectedItem(item);
    setDeletePopup(true);
  };

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setSelectedItem(null);
  };

  const handleDelete = async () => {
    if (!selectedItem || mutationLoading) return;
    const response = await dispatch(
      deleteVendorProductAction(selectedItem.apiId || selectedItem.productId || selectedItem.id),
    );
    const status = response?.status ?? response?.response?.status;
    if (status >= 200 && status < 300) {
      if (productFilter === String(selectedItem.id)) setProductFilter("");
      closeDeletePopup();
      const nextPage = products.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) setPage(nextPage);
      else dispatch(getVendorProductsAction({ page, limit }));
    }
  };

  return (
    <>
      <div className="dashboard-page inquiries-page">
        {/* =========================
            PAGE HEADER
        ========================= */}
        <div className="inquiries-page-header">
          <div>
            <h1 className="f-s-28 l-h-32 fw-semibold">{t("ui.product.meu6z3")}</h1>

            <p className="f-s-14 l-h-20">{t("ui.create.and.manage.your.products.pricing.and.product.information")}</p>
          </div>

          <div>
            <button
              type="button"
              className="recent-view-all-btn"
              onClick={() => openProductPage("add")}
            >{t("ui.add.new.product")}</button>
          </div>
        </div>

        {/* =========================
            STATS
        ========================= */}
        <div className="container-fluid px-0 mb-4">
          <div className="row g-3">
            {productStats.map((stat, index) => (
              <div className="col-md-3" key={index}>
                <div className="overview-stat-card">
                  <div className={`overview-stat-icon ${stat.color}`}>
                    <span
                      className={`overview-stat-icon-img ${stat.color}`}
                      style={{
                        "--icon-url": `url(${stat.icon})`,
                      }}
                    />
                  </div>

                  <div className="overview-stat-content">
                    <span className="overview-stat-title">{text(stat.title)}</span>

                    <div className="overview-stat-bottom">
                      <strong>{stat.value}</strong>

                      <small className={stat.color}>{text(stat.change)}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* =========================
            TABLE CARD
        ========================= */}
        <div className="inquiries-table-card">
          <div className="inquiries-table-header">
            <div>
              <h2>{t("ui.product.meu6z3")}</h2>

              <p>{t("ui.view.and.manage.your.products")}</p>
            </div>

            <div className="inquiries-table-actions">
              {/* =====================
                  SEARCH
              ===================== */}
              <div className="inquiries-search">
                <img src="/assets/search-icon.svg" alt="" />

                <input
                  type="text"
                  placeholder={t("ui.search.products")}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />

                {search && (
                  <button type="button" onClick={() => setSearch("")}>
                    ×
                  </button>
                )}
              </div>

              {/* =====================
                  SECTOR FILTER
              ===================== */}
              <ProductTableDropdown
                label={t("ui.allSectors")}
                value={sectorFilter}
                options={[
                  { value: "", label: t("ui.allSectors") },
                  ...sectorOptions.map((sector) => ({ value: sector, label: sector })),
                ]}
                onChange={handleSectorChange}
              />

              {/* =====================
                  PRODUCT FILTER
                  DEPENDS ON SECTOR
              ===================== */}
              <ProductTableDropdown
                label={sectorFilter ? t("ui.allProducts") : t("ui.selectSectorFirst")}
                value={productFilter}
                disabled={!sectorFilter}
                options={[
                  {
                    value: "",
                    label: sectorFilter ? t("ui.allProducts") : t("ui.selectSectorFirst"),
                  },
                  ...productOptions.map((item) => ({
                    value: String(item.id || item._id),
                    label: item.productName || item.templateName || "Unnamed Product",
                  })),
                ]}
                onChange={(value) => {
                  setProductFilter(value);
                  setPage(1);
                }}
              />

              {/* =====================
                  STATUS FILTER
              ===================== */}
              <div
                className={"inquiries-status-filter" + (statusDropdownOpen ? " is-open" : "")}
                ref={statusDropdownRef}
              >
                <button
                  type="button"
                  className="inquiries-status-trigger"
                  aria-label={t("ui.filter.products.by.status")}
                  aria-haspopup="listbox"
                  aria-expanded={statusDropdownOpen}
                  onClick={() => setStatusDropdownOpen((open) => !open)}
                >
                  <span>{statusFilter ? translateStatus(statusFilter) : t("ui.allStatus")}</span>
                  <svg viewBox="0 0 14 14" aria-hidden="true">
                    <path d="m3.5 5.25 3.5 3.5 3.5-3.5" />
                  </svg>
                </button>
                {statusDropdownOpen && (
                  <div
                    className="inquiries-status-menu"
                    role="listbox"
                    aria-label={t("ui.filter.products.by.status")}
                  >
                    {["", "Active", "Draft"].map((status) => (
                      <button
                        type="button"
                        key={status || "all"}
                        className={"inquiries-status-option" + (statusFilter === status ? " is-selected" : "")}
                        role="option"
                        aria-selected={statusFilter === status}
                        onClick={() => {
                          setStatusFilter(status);
                          setPage(1);
                          setStatusDropdownOpen(false);
                        }}
                      >
                        {status ? translateStatus(status) : t("ui.allStatus")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="inquiries-table-wrapper">
            <table className="inquiries-modern-table operations-modern-table">
              <thead>
                <tr>
                  <th>{t("ui.image.164ncr")}</th>
                  <th>{t("ui.name")}</th>
                  <th>{t("ui.sector.14w3j46")}</th>
                  <th>{t("ui.quantity")}</th>
                  <th>{t("ui.price")}</th>
                  <th>{t("ui.status")}</th>
                  <th className="inquiry-actions-heading">{t("ui.actions.8bdtct")}</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <DataTableLoadingRow colSpan={7} label={t("ui.loadingProducts")} />
                ) : error ? (
                  <tr><td colSpan="7"><div className="inquiries-empty-state" role="alert"><strong>{t("ui.unable.to.load.products")}</strong><span>{text(error)}</span></div></td></tr>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((item) => (
                    <tr key={item.id || item._id}>
                      {/* IMAGE */}
                      <td>
                        {item.mainImage ? (
                          <img
                            src={item.mainImage}
                            alt={
                            item.productName || item.templateName || t("ui.product")
                            }
                            style={{
                              width: "50px",
                              borderRadius: "8px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span>—</span>
                        )}
                      </td>

                      {/* NAME */}
                      <td>
                        <strong>
                          {item.productName || item.templateName || "—"}
                        </strong>
                      </td>

                      {/* SECTOR */}
                      <td>{item.category || item.commodity || "—"}</td>

                      <td>
                        {item.quantity}
                        {item.quantityUnit ? ` ${item.quantityUnit}` : ""}
                      </td>

                      {/* PRICE */}
                      <td>
                        <strong className="transaction-hours">
                          {item.price !== "" &&
                          item.price !== null &&
                          item.price !== undefined
                            ? `${item.currencySymbol || getCurrencySymbol(
                                item.currency,
                              )} ${item.price}`
                            : "—"}
                        </strong>
                      </td>

                      {/* STATUS */}
                      <td>
                        <div className="operation-stage-cell">
                          <span
                            className={`operation-stage-status ${
                              String(item.status || "").toLowerCase() ===
                              "active"
                                ? "approved"
                                : "pending"
                            }`}
                          >
                            {translateStatus(item.status || "Draft")}
                          </span>
                        </div>
                      </td>

                      {/* ACTION */}
                      <td>
                        <div className="operation-table-actions">
                          <button
                            type="button"
                            className="operation-view-btn"
                            onClick={() => openProductPage("view", item)}
                            title={t("ui.view")}
                          >
                            <img src="/assets/eye.svg" alt={t("ui.view")} />
                          </button>

                          <button
                            type="button"
                            className="operation-view-btn"
                            onClick={() => openProductPage("edit", item)}
                            title={t("ui.edit.1a6ui")}
                          >
                            <img src="/assets/edit-icon.svg" alt={t("ui.edit.1a6ui")} />
                          </button>

                          <button
                            type="button"
                            className="operation-view-btn"
                            onClick={() => openDeletePopup(item)}
                            title={t("ui.delete")}
                          >
                            <img src="/assets/trash.svg" alt={t("ui.delete")} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">
                      <div className="inquiries-empty-state">
                        <img src="/assets/search-icon.svg" alt="" />

                        <strong>{t("ui.no.products.found")}</strong>

                        <span>{t("ui.try.changing.your.sector.product.status.or.search")}</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* =========================
              TABLE FOOTER
          ========================= */}
          <div className="inquiries-table-footer">
            <span>{t("ui.showing")}{" "}{firstItem}{" "}{t("ui.to")}{" "}{lastItem}{" "}{t("ui.of")}{" "}<strong>{total}</strong>{" "}{t("ui.products")}</span>

            <div className="inquiries-pagination">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                disabled={currentPage <= 1 || loading}
                aria-label={t("ui.previous.page")}
              >
                {"\u2039"}
              </button>
              {pageNumbers.map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  className={pageNumber === currentPage ? "active" : ""}
                  onClick={() => setPage(pageNumber)}
                  disabled={loading}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                disabled={currentPage >= totalPages || loading}
                aria-label={t("ui.next.page")}
              >
                {"\u203A"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =========================
          DELETE POPUP
      ========================= */}
      {deletePopup && selectedItem && (
        <div className="delete-popup-overlay" onClick={closeDeletePopup}>
          <div
            className="delete-popup-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="delete-popup-close"
              onClick={closeDeletePopup}
              aria-label={t("ui.close")}
            >
              ×
            </button>

            <div className="delete-popup-icon">
              <img src="/assets/trash.svg" alt="" />
            </div>

            <div className="delete-popup-content">
              <h3 className="mt-3">{t("ui.delete")}</h3>

              <p>{t("ui.are.you.sure.you.want.to.delete")}{" "}
                <strong>
                  {selectedItem.productName || selectedItem.templateName}
                </strong>{t("ui.this.product.will.be.permanently.removed.and.this.action.cannot.be.undone")}</p>
            </div>

            <div className="row g-2 delete-popup-actions">
              <div className="col-6">
                <button
                  type="button"
                  className="delete-popup-cancel"
                  onClick={closeDeletePopup}
                >{t("ui.cancel")}</button>
              </div>

              <div className="col-6">
                <button
                  type="button"
                  className="delete-popup-confirm"
                  onClick={handleDelete}
                  disabled={mutationLoading}
                >{t("ui.delete.product")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
